import {
  DashboardFilters,
  DashboardId,
  DashboardInterval,
  DashboardResponse,
  DashboardWidgetId,
  DashboardWidgetResponse,
  WidgetResultResponse,
  DashboardEmailId,
  DashboardEmailResponse,
  DashboardTemplateResponse,
  DashboardSettingResponse,
} from '@business/entities/dashboard';
import { ReportBuilderFormData } from '@modules/dynamicReports/ReportForms';
import { Endpoint } from '../endpoint';
import { GraphTypes } from '@business/entities/graph';
import { R } from '@core/utils/r';

export function fetchDashboardTemplates() {
  return Endpoint.get<DashboardTemplateResponse[]>(`@gc/dashboards/templates`, {});
}

export function fetchDashboards(isCompact?: boolean) {
  const params = R.objectCompact({ isCompact });
  return Endpoint.get<DashboardResponse[]>(`@gc/dashboards`, { params });
}

export function fetchDashboard(id: DashboardId, requestId: number, filters?: DashboardFilters) {
  return Endpoint.get<DashboardResponse>(`@gc/dashboards/dashboard/${id}`, { params: { ...filters }, headers: { requestId: requestId.toString() } });
}

export function createDashboard(data: Partial<DashboardResponse>) {
  return Endpoint.post<DashboardResponse>(`@gc/dashboards`, { data });
}

export function updateDashboard(id: DashboardId, data: Partial<DashboardResponse>) {
  return Endpoint.patch<DashboardResponse>(`@gc/dashboards/${id}`, { data });
}

export function makeDefault(id: DashboardId, interval: DashboardInterval, isDefault: boolean) {
  return Endpoint.patch<DashboardResponse>(`@gc/dashboards/default/${id}`, { data: { interval, isDefault } });
}

export function duplicateDashboard(id: DashboardId, data: Partial<DashboardResponse>) {
  return Endpoint.post<DashboardResponse>(`@gc/dashboards/duplicate/${id}`, { data });
}

export function deleteDashboard(DashboardId: DashboardId) {
  return Endpoint.delete<void>(`@gc/dashboards/${DashboardId}`);
}

/* Widgets */

export function fetchWidget(id: DashboardWidgetId, dateRange?: string) {
  return Endpoint.get<DashboardWidgetResponse>(`@gc/dashboards/widget/${id}`, { params: R.objectCompact({ dateRange }) });
}

export function updateGraphType(id: DashboardWidgetId, type: GraphTypes, dateRange?: string) {
  return Endpoint.patch<DashboardWidgetResponse>(`@gc/dashboards/graph-type/${id}`, { params: { type, dateRange } });
}

export function createWidget(id: DashboardId, settings: ReportBuilderFormData) {
  return Endpoint.post<DashboardResponse>(`@gc/dashboards/widget/${id}`, { data: { settings } });
}

export function updateWidget(id: DashboardWidgetId, settings: Partial<ReportBuilderFormData>) {
  return Endpoint.patch<DashboardResponse>(`@gc/dashboards/widget/${id}`, { data: { settings } });
}

export function updateAllWidgets(id: DashboardId, nWidgets: DashboardWidgetResponse[]) {
  const widgets = nWidgets.map(w => ({ ...w, settings: { ...w.settings, source: w.settings.source.view } }));
  return Endpoint.patch<DashboardResponse>(`@gc/dashboards/bulk-widgets/${id}`, { data: { widgets } });
}

export function deleteWidget(id: DashboardWidgetId) {
  return Endpoint.delete<void>(`@gc/dashboards/widget/${id}`);
}

export function getStatByWidget(widgetId: DashboardWidgetId, interval: DashboardInterval) {
  return Endpoint.post<WidgetResultResponse>(`@gc/widget/result-by-id/${widgetId}`, { data: { interval } });
}

export function getStatByConfig(data: Partial<ReportBuilderFormData>, interval: DashboardInterval) {
  return Endpoint.post<WidgetResultResponse>(`@gc/widget/result-by-data`, { data: { ...data, interval } });
}

export function fetchDashboardPublic(id: DashboardId, timezone: string, dateRange?: string | null) {
  return Endpoint.get<DashboardResponse>(`@gc/public/dashboard/${id}`, {
    params: dateRange ? { dateRange, isCustom: true } : undefined,
    headers: { timezone },
  });
}

export function printDashboard(id: DashboardId, filename: string, dateRange: string, byWidget: boolean, landscape: boolean) {
  return Endpoint.get<DashboardResponse>(`@gc/dashboards/print/${id}`, { isFile: true, filename, params: { dateRange, byWidget, landscape } });
}

/* Dashboard Emails */

export function fetchDashboardEmails(id: DashboardId) {
  return Endpoint.get<DashboardEmailResponse[]>(`@gc/dashboards/emails/${id}`);
}

export function fetchDashboardEmail(id: DashboardEmailId) {
  return Endpoint.get<DashboardEmailResponse>(`@gc/dashboards/email/${id}`);
}

export function createDashboardEmail(id: DashboardId, data: Partial<DashboardEmailResponse>) {
  return Endpoint.post<DashboardEmailResponse>(`@gc/dashboards/email/${id}`, { data });
}

export function updateDashboardEmail(id: DashboardEmailId, data: Partial<DashboardEmailResponse>) {
  return Endpoint.patch<DashboardEmailResponse>(`@gc/dashboards/email/${id}`, { data });
}

export function deleteDashboardEmail(id: DashboardEmailId) {
  return Endpoint.delete<void>(`@gc/dashboards/email/${id}`);
}

export function updateDashboardSettings(id: DashboardId, data: Partial<DashboardSettingResponse>) {
  return Endpoint.patch<DashboardResponse>(`@gc/dashboards/settings/${id}`, { data });
}
